import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  FOLDER_ID_CUSTOM,
  FOLDER_ID_RECOMMENDED,
  MODEL_TYPES,
  QUESTIONS_PER_PAGE,
  readQuestionFolder,
} from '@api/instant_survey';

import {
  Announcement,
  Breadcrumbs,
  Button,
  Error,
  LoaderSkeleton,
  ModalV2 as Modal,
  Pagination,
  Search,
  Tabs,
  useDebounce,
} from '@utilities';

import Results from './components/Results';

import styles from './_index.module.scss';

import { MAX_SCREENER_QUESTIONS } from '../../../../utilities/constants';
import { getQuestionCost, hasQuestionImage } from '../../../../utilities/helpers';

const getFolders = (country) => {
  const isKorean = country?.toLowerCase() === 'kr';

  return [
    { label: isKorean ? '추천 문항' : 'Recommended', value: FOLDER_ID_RECOMMENDED },
    { label: isKorean ? '일반 문항' : 'Custom', value: FOLDER_ID_CUSTOM },
  ];
};

const ModalQuestionLibrary = ({
  allowedQuestionTypes = [],
  areImageQuestionsDisabled = false,
  country,
  getQuestionCostForValidation = getQuestionCost,
  imageQuestionsDisabledTooltip = '',
  maxQuestions,
  onClose,
  onSelectQuestions,
  selectedQuestions,
  surveyType,
}) => {
  const folders = getFolders(country);
  const [activeTab, setActiveTab] = useState(folders[0].value); // this could possibly be selectedFolders[0]
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [questionsToAdd, setQuestionsToAdd] = useState([]);
  const [results, setResults] = useState([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchPage, setSearchPage] = useState(0);
  const [searchQuery, setSearchQuery, { isDebouncing, signal }] = useDebounce('');
  const searchSignal = useRef(null);
  const [selectedFolders, setSelectedFolders] = useState([folders[0]]);

  const selectedFolderId = selectedFolders[selectedFolders.length - 1].value;

  const allowedQuestionTypeValues = allowedQuestionTypes.map(
    (questionType) => questionType.value ?? questionType
  );
  const getQuestionsCostForValidation = (questions = []) =>
    questions.reduce((count, question) => count + getQuestionCostForValidation(question), 0);
  const selectedQuestionsCount = getQuestionsCostForValidation(selectedQuestions);

  const getScreenerCount = (questions = []) =>
    questions.filter(({ _delete, isScreener }) => !_delete && isScreener).length;
  const canSelectScreeners =
    getScreenerCount(selectedQuestions) + getScreenerCount(questionsToAdd) < MAX_SCREENER_QUESTIONS;

  useEffect(() => {
    if (searchPage !== 0) setSearchPage(0);
  }, [selectedFolderId, signal]);

  useEffect(() => {
    getQuestions();
    return () => searchSignal.current?.abort();
  }, [searchPage, selectedFolderId, signal]);

  // const availableQuestions = useMemo(() => {
  //   return results.filter((question) => !selectedQuestions.some(({ id }) => id === question.id));
  // }, [results, selectedQuestions]);

  const getQuestions = async () => {
    if (searchSignal.current) {
      searchSignal.current.abort();
    }
    searchSignal.current = new AbortController();
    setIsSearching(true);
    setError(null);

    try {
      const response = await readQuestionFolder({
        folder: selectedFolderId,
        instantSurveyType: surveyType.id,
        offset: searchPage > 0 ? searchPage * QUESTIONS_PER_PAGE : null,
        ...(searchQuery ? { search: searchQuery } : {}),
        signal: searchSignal.current.signal,
      });
      setSearchCount(response.count);
      setResults(response.contents);
      setIsSearching(false);
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        setError(error);
        setIsSearching(false);
      }
    }
  };

  const handleTabSelect = (value) => {
    setActiveTab(value);
    setSearchPage(0);
    setSelectedFolders([folders.find((folder) => folder.value === value)]);
  };

  const onSelectQuestion = (question) => {
    if (question.type === MODEL_TYPES.QUESTION_FOLDER) {
      setSelectedFolders([...selectedFolders, { label: question.title, value: question.id }]);
    } else {
      const selectedQuestion = results.find(({ id }) => id === question.id);
      if (!selectedQuestion) return;
      if (
        allowedQuestionTypeValues.length > 0 &&
        !allowedQuestionTypeValues.includes(selectedQuestion.type)
      ) {
        return;
      }
      if (areImageQuestionsDisabled && hasQuestionImage(selectedQuestion)) return;

      const questionIndex = questionsToAdd.findIndex(({ id }) => id === question.id);
      const questionsToAddCount = getQuestionsCostForValidation(questionsToAdd);
      if (questionIndex >= 0) {
        questionsToAdd.splice(questionIndex, 1);
        setQuestionsToAdd([...questionsToAdd]);
      } else if (
        (canSelectScreeners || !selectedQuestion.isScreener) &&
        selectedQuestionsCount +
          questionsToAddCount +
          getQuestionCostForValidation(selectedQuestion) <=
          maxQuestions
      ) {
        setQuestionsToAdd([...questionsToAdd, selectedQuestion]);
      }
    }
  };

  const countToAddExceedsMax =
    selectedQuestionsCount + getQuestionsCostForValidation(questionsToAdd) > maxQuestions;

  const actionButtons = [
    <Button key="modal-cancel-button" onClick={onClose} text="Cancel" variant="secondary" />,
    <Button
      isDisabled={!questionsToAdd.length || countToAddExceedsMax}
      key="modal-confirm-button"
      onClick={() => onSelectQuestions(questionsToAdd)}
      text="Add to Survey"
    />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      className="question-library-modal"
      isActive
      onClose={onClose}
      size="large"
      title="Question Library"
    >
      {error ? (
        <Error status={error?.response?.status} />
      ) : (
        <>
          {countToAddExceedsMax && (
            <Announcement
              text={`You have exceeded the maximum number of questions allowed (${maxQuestions}). Remove a question from your survey to continue.`}
              variant="warn"
            />
          )}
          <Search
            className={styles['modal-questions-library-search']}
            isSearching={(isDebouncing || isSearching) && searchQuery !== ''}
            onChange={setSearchQuery}
            value={searchQuery}
          />
          <Tabs
            aria-label="Question library source"
            onChange={handleTabSelect}
            options={folders}
            value={activeTab}
          />
          <div className={styles['modal-questions-library-list']}>
            {selectedFolders.length > 1 && (
              <Breadcrumbs
                options={[
                  ...selectedFolders.slice(1).map(({ label }, index) => ({
                    label,
                    onClick: () => setSelectedFolders(selectedFolders.slice(0, index + 1)),
                  })),
                ]}
              />
            )}
            {(isDebouncing || isSearching) && (
              <LoaderSkeleton>
                <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
                <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
                <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
                <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
                <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
              </LoaderSkeleton>
            )}
            {!(isDebouncing || isSearching) && results && (
              <>
                <Results
                  allowedQuestionTypes={allowedQuestionTypeValues}
                  areImageQuestionsDisabled={areImageQuestionsDisabled}
                  canSelectScreeners={canSelectScreeners}
                  getQuestionCostForValidation={getQuestionCostForValidation}
                  imageQuestionsDisabledTooltip={imageQuestionsDisabledTooltip}
                  isSearching={isDebouncing || isSearching}
                  maxSelectableQuestions={maxQuestions - selectedQuestionsCount}
                  onSelectQuestion={onSelectQuestion}
                  questions={results}
                  questionsToAdd={questionsToAdd}
                  searchQuery={searchQuery}
                  selectedQuestions={selectedQuestions}
                />
                {searchCount > QUESTIONS_PER_PAGE && (
                  <Pagination
                    forcePage={searchPage}
                    onPageChange={(event) => setSearchPage(event.selected)}
                    pageCount={Math.ceil(searchCount / QUESTIONS_PER_PAGE)}
                    show
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

ModalQuestionLibrary.propTypes = {
  allowedQuestionTypes: PropTypes.array,
  areImageQuestionsDisabled: PropTypes.bool,
  country: PropTypes.string,
  getQuestionCostForValidation: PropTypes.func,
  imageQuestionsDisabledTooltip: PropTypes.string,
  maxQuestions: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectQuestions: PropTypes.func.isRequired,
  selectedQuestions: PropTypes.array.isRequired,
  surveyType: PropTypes.object.isRequired,
};

export default ModalQuestionLibrary;
